import { format, parseISO } from 'date-fns';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer,
} from 'recharts';

const INSTRUMENT_CONFIG = {
  phq9: {
    label: 'PHQ-9 — Depression',
    maxScore: 27,
    color: '#6366f1',
    thresholds: [
      { score: 5, label: 'Mild', color: '#fbbf24' },
      { score: 10, label: 'Moderate', color: '#f97316' },
      { score: 15, label: 'Mod. Severe', color: '#ef4444' },
      { score: 20, label: 'Severe', color: '#b91c1c' },
    ],
  },
  gad7: {
    label: 'GAD-7 — Anxiety',
    maxScore: 21,
    color: '#8b5cf6',
    thresholds: [
      { score: 5, label: 'Mild', color: '#fbbf24' },
      { score: 10, label: 'Moderate', color: '#f97316' },
      { score: 15, label: 'Severe', color: '#ef4444' },
    ],
  },
  pcl5: {
    label: 'PCL-5 — PTSD',
    maxScore: 80,
    color: '#ec4899',
    thresholds: [
      { score: 33, label: 'PTSD likely', color: '#ef4444' },
    ],
  },
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-neutral-900 dark:text-neutral-100">
        {format(parseISO(d.fullDate), 'MMM d, yyyy')}
      </p>
      <p className="mt-1">
        <span className="text-neutral-500 dark:text-neutral-400">Score: </span>
        <span className="font-bold text-neutral-900 dark:text-neutral-100">{d.score}</span>
      </p>
      {d.interpretation && (
        <p className="text-neutral-500 dark:text-neutral-400 mt-0.5">{d.interpretation}</p>
      )}
    </div>
  );
};

function InstrumentChart({ formKey, title, responses }) {
  const config = INSTRUMENT_CONFIG[formKey] || { maxScore: null, color: '#6366f1', thresholds: [] };

  const data = [...responses]
    .filter((r) => r.score != null && r.submittedAt)
    .sort((a, b) => new Date(a.submittedAt) - new Date(b.submittedAt))
    .map((r) => ({
      date: format(parseISO(r.submittedAt), 'M/d/yy'),
      score: r.score,
      interpretation: r.scoreInterpretation,
      fullDate: r.submittedAt,
    }));

  if (data.length === 0) return null;

  const maxY = config.maxScore || Math.max(...data.map((d) => d.score)) + 5;

  return (
    <div className="card">
      <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
        {title || config.label}
      </h3>
      <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
        {data.length} administration{data.length !== 1 ? 's' : ''}
        {data.length >= 2 && (
          <span className={`ml-2 font-medium ${
            data[data.length - 1].score < data[0].score
              ? 'text-green-600 dark:text-green-400'
              : data[data.length - 1].score > data[0].score
                ? 'text-red-600 dark:text-red-400'
                : 'text-neutral-500 dark:text-neutral-400'
          }`}>
            {data[data.length - 1].score < data[0].score ? '▼' : data[data.length - 1].score > data[0].score ? '▲' : '—'}
            {' '}
            {Math.abs(data[data.length - 1].score - data[0].score)} pts from first
          </span>
        )}
      </p>
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={data} margin={{ top: 5, right: 24, left: -10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-neutral-100 dark:stroke-neutral-700" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11 }}
            className="fill-neutral-500 dark:fill-neutral-400"
          />
          <YAxis
            domain={[0, maxY]}
            tick={{ fontSize: 11 }}
            className="fill-neutral-500 dark:fill-neutral-400"
          />
          <Tooltip content={<CustomTooltip />} />
          {config.thresholds.map((t) => (
            <ReferenceLine
              key={t.score}
              y={t.score}
              stroke={t.color}
              strokeDasharray="4 2"
              label={{ value: t.label, position: 'insideTopRight', fontSize: 10, fill: t.color }}
            />
          ))}
          <Line
            type="monotone"
            dataKey="score"
            stroke={config.color}
            strokeWidth={2.5}
            dot={{ r: 5, fill: config.color, strokeWidth: 0 }}
            activeDot={{ r: 7 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function OutcomeTrendChart({ responses }) {
  const byKey = {};
  responses.forEach((r) => {
    const key = r.form?.formKey;
    if (!key) return;
    if (!byKey[key]) byKey[key] = { title: r.form?.title, responses: [] };
    byKey[key].responses.push(r);
  });

  const scoredGroups = Object.entries(byKey).filter(([, { responses: rs }]) =>
    rs.some((r) => r.score != null),
  );

  if (scoredGroups.length === 0) return null;

  return (
    <div className="space-y-3 mb-6">
      <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">Score Trends</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {scoredGroups.map(([key, { title, responses: rs }]) => (
          <InstrumentChart key={key} formKey={key} title={title} responses={rs} />
        ))}
      </div>
    </div>
  );
}
